import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import axios from 'axios'
import { generalConfig, noCacheConfig } from '../config/general'
import QUERY_KEYS from '../constants/queryKeys'
import { secondsToMilliseconds } from 'date-fns'
import type { SportMarket, SportMarkets } from '../types/markets'
import type { NetworkConfig } from '../types/network'
import { OVERTIME_API_KEY } from '@/config/env'

// without this every request is treated as new even though it has the same response
const marketsCache = { live: [] as SportMarkets }

const overtimeApiConfig = {
  ...noCacheConfig,
  headers: {
    ...noCacheConfig.headers,
    'x-api-key': OVERTIME_API_KEY,
  },
}

const useLiveSportsMarketsQuery = (
  isLiveSelected: boolean,
  networkConfig: NetworkConfig,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<{ live: SportMarkets }>({
    queryKey: [
      ...QUERY_KEYS.LiveSportMarkets(networkConfig.networkId),
      isLiveSelected,
    ],
    queryFn: async () => {
      try {
        const response = await axios.get<
          undefined,
          { data: { markets: SportMarkets } }
        >(
          `${generalConfig.API_URL}/overtime-v2/networks/${networkConfig.networkId}/live-markets`,
          overtimeApiConfig
        )

        const markets: SportMarkets =
          response?.data?.markets || marketsCache.live

        const marketsFlattened: SportMarkets = markets
          .reduce(
            (accumulator: SportMarkets, value) => accumulator.concat(value),
            []
          )
          .map((game: SportMarket) => {
            game.childMarkets = game.childMarkets.map((childMarket: any) => {
              return {
                ...childMarket,
                live: true, // TODO: remove this property from business logic on UI
                maturityDate: new Date(childMarket.maturityDate),
                odds: childMarket.odds.map((odd: any) => odd.normalizedImplied),
              }
            })

            return {
              ...game,
              live: true, // TODO: remove this property from business logic on UI(we can leave this one for parent, but child markets shouldnt have live flag)
              maturityDate: new Date(game.maturityDate),
              odds: game.odds.map((odd: any) => odd.normalizedImplied),
            }
          })
        marketsCache.live = marketsFlattened
        return { live: marketsFlattened }
      } catch (e) {
        console.log(e)
      }

      return marketsCache
    },
    refetchInterval: secondsToMilliseconds(isLiveSelected ? 10 : 30),
    ...options,
  })
}

export default useLiveSportsMarketsQuery
